import React from 'react';
import SelectFilter from 'components/Select/SelectFilter';
import { I18n } from 'react-redux-i18n';

interface Option {
  value: string | number;
  label: string;
  icon?: string;
}

interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  selectedOptions?: Option[];
  options: Option[];
  // eslint-disable-next-line no-unused-vars
  onChange: (selectedOptions: Option[]) => void;
  isClearable?: boolean;
  isDisabled?: boolean;
  colorLabel?: string;
  fontSize?: number;
  // eslint-disable-next-line no-unused-vars
  renderOptionIcon?: (option: Option) => React.ReactNode;
  showSelectionCount?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  selectedOptions = [],
  options,
  onChange,
  renderOptionIcon,
  showSelectionCount = false,
  ...props
}) => {
  const handleChange = (selectedValues: Option[] | null) => {
    onChange(selectedValues || []);
  };

  // Custom ValueContainer component
  const CustomValueContainer = ({ children }: any) => {
    if (showSelectionCount && selectedOptions.length > 0) {
      // Filter out MultiValue components and keep only Input
      const inputChild = children.find(
        (child: any) => child && child.type && child.type.name === 'Input'
      );

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '25px',
            paddingLeft: 0,
            marginLeft: 0,
            color: '#e25c32',
            fontFamily: 'Oswald',
            fontSize: '14px'
          }}
        >
          ({selectedOptions.length}) {I18n.t('globalView.filters.selected')}
          {/* Hide the input but keep it for react-select functionality */}
          <div style={{ opacity: 0, width: 0, height: 0, overflow: 'hidden' }}>
            {inputChild}
          </div>
        </div>
      );
    }

    if (showSelectionCount && selectedOptions.length === 0) {
      // Show 'All' when no options are selected
      const inputChild = children.find(
        (child: any) => child && child.type && child.type.name === 'Input'
      );

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '25px',
            paddingLeft: 0,
            marginLeft: 0,
            color: '#e25c32',
            fontFamily: 'Oswald',
            fontSize: '14px'
          }}
        >
          {I18n.t('globalView.filters.all')}
          {/* Hide the input but keep it for react-select functionality */}
          <div style={{ opacity: 0, width: 0, height: 0, overflow: 'hidden' }}>
            {inputChild}
          </div>
        </div>
      );
    }

    // Default behavior
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          height: '25px',
          paddingLeft: 0,
          marginLeft: 0,
          color: '#e25c32'
        }}
      >
        {children}
      </div>
    );
  };

  // Custom Option component to show checkmarks for selected items
  const CustomOption = ({ children, ...props }: any) => {
    const { isSelected, data } = props;
    return (
      <div
        {...props.innerProps}
        style={{
          padding: '8px 12px',
          backgroundColor: isSelected ? 'rgba(226, 93, 50, 0.3)' : '#1a1f24',
          color: 'white',
          fontFamily: 'Oswald',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(props.isFocused &&
            !isSelected && { backgroundColor: 'rgba(0, 0, 0, 0.60)' }),
          ...(props.isFocused &&
            isSelected && { backgroundColor: 'rgba(226, 93, 50, 0.5)' })
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderOptionIcon && renderOptionIcon(data)}
          <span>{children}</span>
        </div>
        {isSelected && (
          <span
            style={{ fontWeight: 'bold', fontSize: '16px', color: 'white' }}
          >
            ✓
          </span>
        )}
      </div>
    );
  };

  // Custom placeholder that shows default text when not showing count
  const getPlaceholder = () => {
    if (showSelectionCount && selectedOptions.length > 0) {
      return ''; // Empty since we handle this in ValueContainer
    }
    return props.placeholder || '';
  };

  return (
    <SelectFilter
      selectedOption={selectedOptions} // For multi-select, this will be an array
      dropdownProps={{
        colorIcon: '#2b3d54'
      }}
      minWidth="80px"
      containerStyles={`.label{color:${
        props.colorLabel ? props.colorLabel : '#2b3d54'
      };${
        props.fontSize && 'margin-bottom:' + Number(props.fontSize - 20) + 'px'
      }`}
      customStyles={{
        container: {
          marginTop: 5,
          height: 25,
          minHeight: 25
        },
        control: {
          border: 0,
          borderBottom: '1px solid rgba(134,142,150,0.3)',
          backgroundColor: 'transparent',
          height: '25px',
          minHeight: '25px',
          fontFamily: 'Oswald',
          cursor: 'pointer'
        },
        valueContainer: {
          height: 25,
          paddingLeft: 0,
          marginLeft: 0,
          color: '#e25c32'
        },
        multiValue: {
          backgroundColor: '#e25c32',
          color: 'white',
          borderRadius: '4px',
          margin: '2px'
        },
        multiValueLabel: {
          color: 'white',
          fontFamily: 'Oswald',
          fontSize: props.fontSize ? props.fontSize : '12px',
          padding: '2px 6px'
        },
        multiValueRemove: {
          color: 'white',
          ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white'
          }
        },
        placeholder: {
          fontStyle: 'normal',
          color: 'rgba(43, 61, 84, 0.6)',
          fontSize: props.fontSize ? props.fontSize : '14px'
        },
        option: {
          padding: 0, // Remove default padding since we handle it in CustomOption
          backgroundColor: 'transparent'
        },
        menuList: {
          backgroundColor: 'rgba(0, 0, 0, 0.53)',
          padding: 0
        }
      }}
      components={{
        ValueContainer: CustomValueContainer,
        Option: CustomOption
      }}
      value={selectedOptions}
      options={options}
      onChange={handleChange}
      isMulti={true}
      hideSelectedOptions={false} // Keep selected options visible in dropdown
      placeholder={getPlaceholder()}
      {...props}
    />
  );
};

export default MultiSelect;
